import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import html2canvas from 'html2canvas';
import StudyCard from './StudyCard';
import { shareImage, downloadImage, canvasToBlob, isShareSupported } from '../common/shareService';
import { authFetch } from '../common/authFetch';
import ConfirmationDialog from '../ConfirmationDialog';
import '../css/Dialogs.css';
import './ShareDialog.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const THEMES = ['light', 'dark', 'gradient', 'pastel', 'neon'];
const CARD_NATIVE_WIDTH = 540;

const ShareDialog = ({ open, onClose, cardType = 'daily', data = {} }) => {
  const { t } = useTranslation();
  const [selectedTheme, setSelectedTheme] = useState('light');
  const [showUsername, setShowUsername] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewScale, setPreviewScale] = useState(0.5);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [isUserUploaded, setIsUserUploaded] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const fileInputRef = useRef(null);
  const captureRef = useRef(null);
  const previewContainerRef = useRef(null);

  // iOS Safari: 모달 열릴 때 배경 스크롤 방지
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [open]);

  // 다이얼로그 열릴 때 랜덤 배경 이미지 로드
  useEffect(() => {
    if (!open) {
      setBackgroundImage(null);
      setIsUserUploaded(false);
      return;
    }
    const fetchBg = async () => {
      try {
        const res = await authFetch(`${BACKEND_URL}/api/v1/backgrounds/random`);
        const data = await res.json();
        if (data.success && data.image) {
          setBackgroundImage(`${BACKEND_URL}${data.image.file_path}`);
        }
      } catch (e) {
        // fallback: no background
      }
    };
    fetchBg();
  }, [open]);

  const doChangeBg = async () => {
    try {
      const res = await authFetch(`${BACKEND_URL}/api/v1/backgrounds/random`);
      const data = await res.json();
      if (data.success && data.image) {
        setBackgroundImage(`${BACKEND_URL}${data.image.file_path}`);
        setIsUserUploaded(false);
      } else {
        toast.info(t('share.noBgAvailable'));
      }
    } catch (e) {
      console.error('Failed to fetch background:', e);
    }
  };

  const handleChangeBg = () => {
    if (isUserUploaded) {
      setConfirmDialog({
        isOpen: true,
        title: t('confirm'),
        message: t('share.bgUploadedConfirm'),
        onConfirm: () => { setConfirmDialog(prev => ({ ...prev, isOpen: false })); doChangeBg(); }
      });
      return;
    }
    doChangeBg();
  };

  const handleUploadBg = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const applyFile = () => {
      const url = URL.createObjectURL(file);
      setBackgroundImage(url);
      setIsUserUploaded(true);
    };

    if (isUserUploaded) {
      setConfirmDialog({
        isOpen: true,
        title: t('confirm'),
        message: t('share.bgUploadedConfirm'),
        onConfirm: () => { setConfirmDialog(prev => ({ ...prev, isOpen: false })); applyFile(); }
      });
      return;
    }
    applyFile();
  };

  useEffect(() => {
    if (!open || !previewContainerRef.current) return;

    const updateScale = () => {
      if (!previewContainerRef.current) return;
      const containerWidth = previewContainerRef.current.clientWidth - 24; // subtract padding
      const scale = Math.min(containerWidth / CARD_NATIVE_WIDTH, 0.5);
      setPreviewScale(scale);
    };

    updateScale();

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(updateScale);
    observer.observe(previewContainerRef.current);

    return () => observer.disconnect();
  }, [open]);

  const username = data.username || '';

  const captureCard = useCallback(async () => {
    if (!captureRef.current) return null;
    const canvas = await html2canvas(captureRef.current, {
      scale: 2,
      useCORS: true,
      backgroundColor: null,
      logging: false,
    });
    return canvasToBlob(canvas);
  }, []);

  const handleShare = useCallback(async () => {
    setIsProcessing(true);
    try {
      const blob = await captureCard();
      if (!blob) {
        toast.error(t('share.error'));
        return;
      }
      const result = await shareImage(blob, t('share.cardTitle'));
      if (result === 'unsupported') {
        toast.info(t('share.unsupported'));
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Share failed:', error);
        toast.error(t('share.error'));
      }
    } finally {
      setIsProcessing(false);
    }
  }, [captureCard, t]);

  const handleDownload = useCallback(async () => {
    setIsProcessing(true);
    try {
      const blob = await captureCard();
      if (!blob) {
        toast.error(t('share.error'));
        return;
      }
      downloadImage(blob, `study-card-${data.date || 'export'}.png`);
      toast.success(t('share.downloaded'));
    } catch (error) {
      console.error('Download failed:', error);
      toast.error(t('share.error'));
    } finally {
      setIsProcessing(false);
    }
  }, [captureCard, t, data.date]);

  if (!open) return null;

  return (
    <div className="dialog-overlay" onClick={onClose} data-testid="share-dialog">
      <div className="dialog-box share-dialog-box" onClick={(e) => e.stopPropagation()}>
        <h2 className="dialog-title">{t('share.title')}</h2>

        {/* Theme selector */}
        <div>
          <span className="dialog-label">{t('share.selectTheme')}</span>
          <div className="share-theme-selector" data-testid="theme-selector">
            {THEMES.map((theme) => (
              <button
                key={theme}
                className={`share-theme-btn share-theme-btn-${theme} ${selectedTheme === theme ? 'active' : ''}`}
                onClick={() => setSelectedTheme(theme)}
                title={t(`share.theme.${theme}`)}
                aria-label={t(`share.theme.${theme}`)}
              />
            ))}
          </div>
        </div>

        {/* Options */}
        <div className="share-options">
          <label className="share-option-checkbox">
            <input
              type="checkbox"
              checked={showUsername}
              onChange={(e) => setShowUsername(e.target.checked)}
            />
            {t('share.showUsername')}
          </label>
        </div>

        {/* Background controls */}
        <div className="share-bg-controls">
          <button
            className="share-bg-btn"
            onClick={handleChangeBg}
            disabled={isProcessing}
          >
            {t('share.changeBg')}
          </button>
          <button
            className="share-bg-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={isProcessing}
          >
            {t('share.uploadBg')}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleUploadBg}
            style={{ display: 'none' }}
          />
          {backgroundImage && (
            <button
              className="share-bg-btn share-bg-btn-remove"
              onClick={() => { setBackgroundImage(null); setIsUserUploaded(false); }}
              disabled={isProcessing}
            >
              {t('share.removeBg')}
            </button>
          )}
        </div>

        {/* Preview */}
        <div
          className="share-preview-container"
          ref={previewContainerRef}
          style={{ height: CARD_NATIVE_WIDTH * previewScale + 24 }}
        >
          <div className="share-preview-wrapper" style={{ transform: `scale(${previewScale})`, width: CARD_NATIVE_WIDTH, height: CARD_NATIVE_WIDTH }}>
            <StudyCard
              cardType={cardType}
              data={data}
              theme={selectedTheme}
              showUsername={showUsername}
              username={username}
              backgroundImage={backgroundImage}
            />
          </div>
        </div>

        {/* Hidden capture element (full size) */}
        <div className="share-capture-container">
          <StudyCard
            cardRef={captureRef}
            cardType={cardType}
            data={data}
            theme={selectedTheme}
            showUsername={showUsername}
            username={username}
            backgroundImage={backgroundImage}
          />
        </div>

        {/* Processing indicator */}
        {isProcessing && (
          <div className="share-loading">{t('share.processing')}</div>
        )}

        {/* Actions */}
        <div className="share-actions">
          <button
            className="btn-secondary"
            onClick={onClose}
            disabled={isProcessing}
          >
            {t('close')}
          </button>
          {isShareSupported() && (
            <button
              className="share-btn-share"
              onClick={handleShare}
              disabled={isProcessing}
              data-testid="share-button"
            >
              {t('share.share')}
            </button>
          )}
          <button
            className="share-btn-download"
            onClick={handleDownload}
            disabled={isProcessing}
            data-testid="download-button"
          >
            {t('share.download')}
          </button>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        confirmLabel={t('confirm')}
      />
    </div>
  );
};

export default ShareDialog;
